import { useEffect } from 'react';
import Icon from './Icon';

export type ClusterNodeContextMenuState = {
  x: number;
  y: number;
  nodeId: string;
  label: string;
  isLeaf: boolean;
  isRoot: boolean;
} | null;

type ClusterNodeContextMenuProps = {
  menu: ClusterNodeContextMenuState;
  locked?: boolean;
  canEdit?: boolean;
  onClose: () => void;
  onAddChild: (nodeId: string) => void;
  onEdit: (nodeId: string) => void;
  onDelete: (nodeId: string) => void;
};

export default function ClusterNodeContextMenu({
  menu,
  locked = false,
  canEdit = true,
  onClose,
  onAddChild,
  onEdit,
  onDelete
}: ClusterNodeContextMenuProps) {
  useEffect(() => {
    if (!menu) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    const onPointer = () => onClose();
    window.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onPointer, true);
    window.addEventListener('resize', onPointer);
    // Delay click-away so the opening contextmenu click does not close it.
    const timer = window.setTimeout(() => {
      window.addEventListener('mousedown', onPointer);
    }, 0);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onPointer, true);
      window.removeEventListener('resize', onPointer);
      window.removeEventListener('mousedown', onPointer);
    };
  }, [menu, onClose]);

  if (!menu || locked || !canEdit) return null;

  const items = [
    {
      key: 'add',
      label: 'افزودن زیرشاخه',
      icon: 'plus-lg' as const,
      disabled: false,
      onClick: () => onAddChild(menu.nodeId)
    },
    {
      key: 'edit',
      label: 'ویرایش نود',
      icon: 'pencil' as const,
      disabled: false,
      onClick: () => onEdit(menu.nodeId)
    },
    {
      key: 'delete',
      label: 'حذف نود',
      icon: 'trash' as const,
      disabled: menu.isRoot,
      danger: true,
      onClick: () => onDelete(menu.nodeId)
    }
  ];

  return (
    <div
      className="cluster-node-context-menu shadow border rounded-2 bg-body py-1"
      style={{
        position: 'fixed',
        top: menu.y,
        left: menu.x,
        zIndex: 1080,
        minWidth: '11.5rem',
        direction: 'rtl'
      }}
      role="menu"
      onMouseDown={event => event.stopPropagation()}
      onContextMenu={event => event.preventDefault()}
    >
      <div className="px-2 py-1 fs--2 text-600 text-truncate border-bottom mb-1">
        {menu.label}
      </div>
      {items.map(item => (
        <button
          key={item.key}
          type="button"
          role="menuitem"
          disabled={item.disabled}
          className={`w-100 border-0 bg-transparent d-flex align-items-center gap-2 px-2 py-1 fs--1 text-start${
            item.danger ? ' text-danger' : ' text-body'
          }`}
          style={{
            cursor: item.disabled ? 'not-allowed' : 'pointer',
            opacity: item.disabled ? 0.45 : 1
          }}
          onClick={() => {
            if (item.disabled) return;
            item.onClick();
            onClose();
          }}
        >
          <Icon name={item.icon} size="0.75rem" />
          <span>{item.label}</span>
        </button>
      ))}
    </div>
  );
}
